// Mediathek: hochladen, messen lassen, einordnen. Was hier als „Musik“ steht, spielt die Automatik.

import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent } from "react";
import { api, libraryCoverBlob } from "../api";
import { errorText } from "../components/streaming-ui";
import { NachrechnenPanel, TaktBadge, TaktEditor, TaktFilter, TAKT_FILTER_LEER, type TaktFilterWert } from "../components/takt";
import { Badge, Callout, Card, EmptyState, Page, PageHeader, Skeleton, Tabs } from "../components/ui";
import { MusikRegelnPanel } from "../components/musikregeln";
import type { LibraryCategory, LibraryItem, LibraryItemWithExtras } from "../types";

export const CATEGORY_LABEL: Record<LibraryCategory, string> = {
  music: "Musik",
  jingle: "Jingle",
  ad: "Werbung",
  news: "Nachrichten",
  speech: "Wortbeitrag",
  voicetrack: "Voice-Track",
};

const CATEGORY_HINT: Record<LibraryCategory, string> = {
  music: "Spielt die Automatik, wenn keine Playlist läuft",
  jingle: "Für Cartwall und Stundenuhr, nie als Musik",
  ad: "Für Werbeblöcke",
  news: "Für Nachrichten-Plätze",
  speech: "Beiträge, Interviews",
  voicetrack: "Vorab aufgenommene Moderationen",
};

// Farbe je Kategorie – damit man in einer langen Liste auf einen Blick sieht,
// was Musik ist und was Jingle, Werbung oder Wort.
const CATEGORY_TONE: Record<LibraryCategory, string> = {
  music: "bg-brand-primary/10 text-brand-primary",
  jingle: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  ad: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
  news: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
  speech: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  voicetrack: "bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300",
};

/** Notensymbol für Platzhalter-Cover und die Upload-Fläche. */
function NoteIcon({ className = "h-5 w-5" }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
    </svg>
  );
}

/** Kleines Cover; ohne Bild ein Platzhalter in der Farbe der Kategorie. */
function CoverThumb({ item }: { item: LibraryItem }): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  // Erst laden, wenn die Zeile ins Bild kommt: Eine lange Mediathek soll
  // nicht hunderte Bilder auf einmal anfragen.
  useEffect(() => {
    if (!item.cover_url || !ref.current) return;
    let url: string | null = null;
    let aus = false;
    const beobachter = new IntersectionObserver((eintraege) => {
      if (!eintraege.some((e) => e.isIntersecting)) return;
      beobachter.disconnect();
      libraryCoverBlob(item.id)
        .then((blob) => {
          if (aus || !blob) return;
          url = URL.createObjectURL(blob);
          setSrc(url);
        })
        .catch(() => undefined);
    }, { rootMargin: "200px" });
    beobachter.observe(ref.current);
    return () => { aus = true; beobachter.disconnect(); if (url) URL.revokeObjectURL(url); };
  }, [item.id, item.cover_url]);
  if (src) {
    return (
      <img src={src} alt="" onError={() => setSrc(null)}
        className="h-10 w-10 flex-shrink-0 rounded-md object-cover ring-1 ring-black/5 dark:ring-white/10" />
    );
  }
  return (
    <div ref={ref} className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ${CATEGORY_TONE[item.category]}`}>
      <NoteIcon className="h-4 w-4" />
    </div>
  );
}

function formatDuration(ms: number | null): string {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function formatSeconds(ms: number): string {
  return `${(ms / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} s`;
}

/** Wellenform mit ausgegrauter Stille vor dem Einstieg und nach dem Ausstieg, Ausklang heller. */
function Waveform({ item, width = 160, height = 28 }: { item: LibraryItem; width?: number; height?: number }): JSX.Element {
  const peaks = (item.waveform_json as { peaks?: number[] } | null)?.peaks ?? [];
  const duration = item.duration_ms ?? 0;
  if (peaks.length === 0 || duration === 0) {
    return <div style={{ width, height }} className="flex-shrink-0 rounded bg-input" />;
  }
  const x = (ms: number | null): number => (ms === null ? 0 : Math.max(0, Math.min(width, (ms / duration) * width)));
  const barW = width / peaks.length;
  const cueIn = x(item.cue_in_ms);
  const cueOut = item.cue_out_ms === null ? width : x(item.cue_out_ms);
  const mixOut = item.mix_out_ms === null ? cueOut : x(item.mix_out_ms);
  return (
    <svg width={width} height={height} className="flex-shrink-0 rounded bg-input" aria-label="Wellenform">
      {peaks.map((p, i) => {
        const px = i * barW;
        const h = Math.max(1, p * (height - 2));
        const outside = px < cueIn || px > cueOut;
        const outro = px >= mixOut && px <= cueOut;
        return (
          <rect key={i} x={px} y={(height - h) / 2} width={Math.max(0.6, barW - 0.2)} height={h}
            className={outside ? "fill-gray-300" : outro ? "fill-amber-400" : "fill-brand-primary"} />
        );
      })}
      {item.cue_in_ms !== null && item.cue_in_ms > 0 && <line x1={cueIn} x2={cueIn} y1={0} y2={height} className="stroke-green-600" strokeWidth={1} />}
      {item.cue_out_ms !== null && <line x1={cueOut} x2={cueOut} y1={0} y2={height} className="stroke-red-500" strokeWidth={1} />}
    </svg>
  );
}

function AnalysisBadge({ item }: { item: LibraryItem }): JSX.Element {
  if (item.analysis_status === "failed") {
    return (
      <Badge tone="danger" dot className="whitespace-nowrap">
        <span title={item.analysis_error ?? ""}>Datei defekt – wird nicht gesendet</span>
      </Badge>
    );
  }
  if (item.analysis_status !== "done") {
    return <Badge tone="info" dot className="animate-pulse whitespace-nowrap">wird gemessen …</Badge>;
  }
  const parts: string[] = [];
  if (item.loudness_lufs !== null) parts.push(`${item.loudness_lufs.toLocaleString("de-DE")} LUFS`);
  if (item.cue_in_ms) parts.push(`Stille am Anfang ${formatSeconds(item.cue_in_ms)}`);
  if (item.cue_out_ms !== null && item.mix_out_ms !== null && item.cue_out_ms - item.mix_out_ms >= 1000) {
    parts.push(`Ausklang ${formatSeconds(item.cue_out_ms - item.mix_out_ms)}`);
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted" title="Gemessen beim Hochladen. Der Sender gleicht die Lautstärke an und blendet am Ausklang über.">
      <span className="text-emerald-600 dark:text-emerald-400">✓</span> {parts.join(" · ") || "gemessen"}
    </span>
  );
}

interface UploadEntry {
  name: string;
  state: "waiting" | "uploading" | "done" | "error";
  message?: string;
}

export function UploadZone({ onUploaded }: { onUploaded: (item: LibraryItem) => void }): JSX.Element {
  const [queue, setQueue] = useState<UploadEntry[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const ordnerRef = useRef<HTMLInputElement>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [gefunden, setGefunden] = useState<string | null>(null);
  const busy = queue.some((q) => q.state === "uploading" || q.state === "waiting");

  // Wird ein ganzer Ordner gewählt, liefert der Browser zu jeder Datei ihren
  // Weg darin mit. Den braucht der Server, um "CD 1 von 2" zu erkennen – und
  // um das Bild aus demselben Ordner dem richtigen Titel zuzuordnen.
  const wegVon = (f: File): string => (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
  const ordnerVon = (f: File): string => { const w = wegVon(f); const i = w.lastIndexOf("/"); return i < 0 ? "" : w.slice(0, i); };

  // Das Bild zu einem Titel: erst im eigenen Ordner, dann eine Ebene hoeher.
  //
  // Bei Doppelalben liegen die Titel in `Album/CD 1` und `Album/CD 2`, das
  // Cover aber einmal in `Album`. Wer nur im eigenen Ordner sucht, findet
  // dann nichts – und genau so war es.
  const bildFuer = (ordner: string, bilder: Map<string, File>): File | undefined => {
    let weg = ordner;
    for (;;) {
      const treffer = bilder.get(weg);
      if (treffer) return treffer;
      const i = weg.lastIndexOf("/");
      if (i < 0) return weg === "" ? undefined : bilder.get("");
      weg = weg.slice(0, i);
    }
  };

  const upload = async (files: File[]): Promise<void> => {
    const audio = files.filter((f) => f.type.startsWith("audio/") || /\.(mp3|mp2|m4a|m4b|aac|ogg|oga|opus|flac|wav|wave|aif|aiff|aifc|wma|alac)$/i.test(f.name));
    // Vorher stand hier ein stilles `return`. Wer einen Ordner waehlt, in dem
    // nichts Passendes liegt, sah dann gar nichts – kein Fortschritt, keine
    // Meldung, nichts. Das ist der unangenehmste aller Fehler.
    if (audio.length === 0) {
      const namen = files.slice(0, 5).map((f) => `„${f.name}“`).join(", ");
      setHinweis(files.length === 0
        ? "Es wurde nichts ausgewählt."
        : `${files.length} Datei(en) gelesen, aber keine Musik darunter: ${namen}` +
          (files.length > 5 ? " …" : "") +
          ". Erkannt werden MP3, M4A/AAC, Ogg, Opus, FLAC, WAV, AIFF, WMA und MP2.");
      return;
    }
    setHinweis(null);
    // Bilder je Ordner einsammeln.
    //
    // Die bekannten Namen gelten zuerst – aber ein Bild, das anders heisst,
    // wird NICHT weggeworfen. Sammlungen tragen ihr Cover unter allem
    // moeglichen: "AlbumArt.jpg", "00 - Front.jpg", oder schlicht unter dem
    // Namen des Albums. Vorher fiel all das durch, ohne ein Wort.
    const bilder = new Map<string, File>();
    const rang = (n: string): number =>
      /^cover\b/i.test(n) ? 0
      : /^folder\b/i.test(n) ? 1
      : /^front\b/i.test(n) ? 2
      : /(cover|front|album ?art)/i.test(n) ? 3
      : 4;
    for (const f of files) {
      if (!/\.(jpe?g|png|webp)$/i.test(f.name)) continue;
      const o = ordnerVon(f);
      const bisher = bilder.get(o);
      // Bei gleichem Rang gewinnt das groessere Bild – kleine Beigaben wie
      // "AlbumArtSmall.jpg" liegen oft neben dem eigentlichen Cover.
      if (!bisher || rang(f.name) < rang(bisher.name)
          || (rang(f.name) === rang(bisher.name) && f.size > bisher.size)) {
        bilder.set(o, f);
      }
    }
    // Zeigen, was erkannt wurde. Beim Ordner-Upload ist das der einzige Weg,
    // vor dem Hochladen zu sehen, ob Pfad und Cover wirklich mitgehen.
    const ordner = new Set(audio.map(ordnerVon));
    const scheibenOrdner = [...ordner].filter((o) =>
      /(?:^|\/)(?:cd|disc|disk|scheibe|vol|volume)[\s._-]*\d/i.test(o));
    const namen = [...new Set([...bilder.values()].map((b) => b.name))];
    const mitBild = audio.filter((f) => bildFuer(ordnerVon(f), bilder) !== undefined).length;
    setGefunden(
      `${audio.length} Titel` +
      (ordner.size > 1 ? ` aus ${ordner.size} Ordnern` : " aus einem Ordner") +
      (bilder.size > 0
        ? `, ${mitBild} davon mit Bild (${namen.slice(0, 3).map((n) => `„${n}“`).join(", ")})`
        : ", kein Bild in den Ordnern – in der Datei eingebettete Cover holt der Server selbst") +
      (scheibenOrdner.length > 0
        ? `, ${scheibenOrdner.length} von ${ordner.size} Ordnern mit Scheibenangabe`
        : ", keine Scheibenangabe in den Ordnernamen")
    );
    const start = queue.length;
    setQueue((q) => [...q, ...audio.map((f) => ({ name: f.name, state: "waiting" as const }))]);
    // Nacheinander, damit ein schwacher Server nicht mit zehn Uploads gleichzeitig kämpft.
    for (let i = 0; i < audio.length; i++) {
      const idx = start + i;
      setQueue((q) => q.map((e, j) => (j === idx ? { ...e, state: "uploading" } : e)));
      try {
        const datei = audio[i]!;
        const created = await api.uploadLibrary(datei, {
          relpath: wegVon(datei),
          cover: bildFuer(ordnerVon(datei), bilder),
        });
        onUploaded(created);
        setQueue((q) => q.map((e, j) => (j === idx ? { ...e, state: "done" } : e)));
      } catch (err) {
        setQueue((q) => q.map((e, j) => (j === idx ? { ...e, state: "error", message: errorText(err) } : e)));
      }
    }
  };

  // Einen gezogenen Ordner auspacken.
  //
  // `dataTransfer.files` enthaelt beim Ziehen eines Ordners GENAU EINEN
  // Eintrag: den Ordner selbst. Der ist keine Datei, hat keine Endung und
  // keinen Typ – frueher hiess es darum "1 Datei gelesen, aber keine Musik".
  // Der Inhalt steht nur ueber `webkitGetAsEntry()` zur Verfuegung, und der
  // muss von Hand durchlaufen werden.
  const ausEintrag = async (eintrag: FileSystemEntry, weg: string): Promise<File[]> => {
    if (eintrag.isFile) {
      const datei = await new Promise<File | null>((ok) =>
        (eintrag as FileSystemFileEntry).file((f) => ok(f), () => ok(null)));
      if (!datei) return [];
      // Den Weg mitgeben, damit "CD 1 von 2" auch beim Ziehen erhalten bleibt.
      Object.defineProperty(datei, "webkitRelativePath", { value: weg + datei.name, configurable: true });
      return [datei];
    }
    if (!eintrag.isDirectory) return [];
    const leser = (eintrag as FileSystemDirectoryEntry).createReader();
    const kinder: FileSystemEntry[] = [];
    // readEntries liefert hoechstens hundert auf einmal – bis es leer bleibt.
    for (;;) {
      const teil = await new Promise<FileSystemEntry[]>((ok) => leser.readEntries((r) => ok(r), () => ok([])));
      if (teil.length === 0) break;
      kinder.push(...teil);
    }
    const alle = await Promise.all(kinder.map((k) => ausEintrag(k, `${weg}${eintrag.name}/`)));
    return alle.flat();
  };

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragging(false);
    const eintraege = Array.from(e.dataTransfer.items ?? [])
      .map((i) => (typeof i.webkitGetAsEntry === "function" ? i.webkitGetAsEntry() : null))
      .filter((x): x is FileSystemEntry => x !== null);
    if (eintraege.length === 0) {
      void upload(Array.from(e.dataTransfer.files));
      return;
    }
    setHinweis(null);
    void (async () => {
      const dateien = (await Promise.all(eintraege.map((x) => ausEintrag(x, "")))).flat();
      await upload(dateien);
    })();
  };

  return (
    <div className="mb-6 space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`group cursor-pointer rounded-xl border-2 border-dashed px-6 py-6 text-center transition sm:py-7 ${dragging ? "border-brand-primary bg-brand-primary/5" : "border-default bg-card hover:border-brand-primary/50 hover:bg-brand-primary/[0.03]"}`}
      >
        <div className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary transition ${dragging ? "scale-110" : "group-hover:scale-105"}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6" aria-hidden="true">
            <path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M20 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2" />
          </svg>
        </div>
        <div className="text-[0.95rem] font-semibold text-primary">
          {dragging ? "Loslassen zum Hochladen" : "Dateien oder ganze Ordner hierher ziehen – oder klicken"}
        </div>
        <div className="mx-auto mt-1.5 max-w-xl text-xs leading-relaxed text-muted">
          MP3, AAC/M4A, Ogg, Opus, FLAC, WAV – auch viele auf einmal. Titel und Interpret kommen aus den Tags der Datei;
          jede Datei wird gemessen und bekommt gleiche Lautstärke.
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <span className="btn btn-primary btn-sm">Dateien wählen</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); ordnerRef.current?.click(); }}
            className="btn btn-secondary btn-sm"
          >
            Ganzen Ordner wählen
          </button>
        </div>
        <div className="mt-2 text-xs text-muted">
          Dann kommt das <code className="rounded bg-input px-1">cover.jpg</code> aus dem Ordner mit, und aus
          „CD&nbsp;1 von 2“ wird die Scheibennummer.
        </div>
        <input ref={inputRef} type="file" accept="audio/*" multiple hidden onChange={(e) => { void upload(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
        {/* Ordnerauswahl: Der Browser liefert dann auch das cover.jpg und den Weg
            "Album/CD 1 von 2/…" mit. `webkitdirectory` steht so nicht im Typ von
            React, ist aber in allen gängigen Browsern vorhanden. */}
        <input
          ref={ordnerRef}
          type="file"
          hidden
          multiple
          {...{ webkitdirectory: "", directory: "" }}
          onChange={(e) => { void upload(Array.from(e.target.files ?? [])); e.target.value = ""; }}
        />
      </div>
      {hinweis && <Callout tone="warning" className="text-xs">{hinweis}</Callout>}
      {gefunden && <Callout tone="info" className="text-xs"><span className="font-medium">Erkannt:</span> {gefunden}</Callout>}
      {queue.length > 0 && (() => {
        const fertig = queue.filter((q) => q.state === "done").length;
        const anteil = Math.round((fertig / queue.length) * 100);
        return (
          <div className="card p-4 text-xs">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="font-medium text-secondary">
                <span className="tabular-nums">{fertig} von {queue.length}</span> hochgeladen
                {queue.some((q) => q.state === "error") && <span className="text-red-600 dark:text-red-400">, {queue.filter((q) => q.state === "error").length} fehlgeschlagen</span>}
              </span>
              {!busy && <button onClick={() => setQueue([])} className="btn btn-ghost btn-sm">Liste leeren</button>}
            </div>
            <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-input">
              <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${anteil}%` }} />
            </div>
            <ul className="max-h-40 space-y-1 overflow-auto">
              {queue.map((q, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-24 flex-shrink-0">
                    {q.state === "done" ? <Badge tone="success">✓ fertig</Badge>
                      : q.state === "uploading" ? <Badge tone="brand" className="animate-pulse">↑ lädt …</Badge>
                      : q.state === "error" ? <Badge tone="danger">✗ Fehler</Badge>
                      : <Badge>wartet</Badge>}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-secondary">{q.name}</span>
                  {q.message && <span className="text-red-600">{q.message}</span>}
                </li>
              ))}
            </ul>
          </div>
        );
      })()}
    </div>
  );
}

export function LibraryPage(): JSX.Element {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<LibraryCategory | "">("");
  const [takt, setTakt] = useState<TaktFilterWert>(TAKT_FILTER_LEER);
  // f-regeln: Reiter „Titel“ / „Regeln“ (Musikregeln). ?reiter=regeln öffnet direkt die Regeln.
  const [reiter, setReiter] = useState<"titel" | "regeln">(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("reiter") === "regeln" ? "regeln" : "titel");

  const load = useCallback(async (): Promise<void> => {
    try {
      setItems(
        await api.listLibrary({
          q: q || undefined,
          category: category || undefined,
          bpm_min: takt.bpm_min ? Number(takt.bpm_min) : undefined,
          bpm_max: takt.bpm_max ? Number(takt.bpm_max) : undefined,
          camelot: takt.camelot || undefined,
          ohne_bpm: takt.ohne_bpm || undefined,
          sort: takt.sort,
          limit: 200,
        })
      );
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [q, category, takt]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Solange noch gemessen wird, regelmäßig nachladen.
  const measuring = items.some((i) => i.analysis_status === "pending" || i.analysis_status === "running");
  useEffect(() => {
    if (!measuring) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [measuring, load]);

  return (
    <Page className="max-w-7xl">
      <PageHeader
        eyebrow="Programm"
        title="Mediathek"
        description="Alles, was gesendet werden kann. Titel der Kategorie „Musik“ spielt die Automatik, wenn gerade keine Playlist dran ist."
        actions={
          <div className="flex items-center gap-2">
            {measuring && <Badge tone="info" dot className="animate-pulse">wird gemessen</Badge>}
            <Badge tone="neutral"><span className="tabular-nums">{items.length}</span>&nbsp;Titel</Badge>
          </div>
        }
      />

      <Tabs
        value={reiter}
        onChange={setReiter}
        className="mb-4"
        items={[{ value: "titel", label: "Titel" }, { value: "regeln", label: "Regeln" }]}
      />

      {reiter === "regeln" ? <MusikRegelnPanel /> : (<>
      <UploadZone onUploaded={(item) => setItems((list) => [item, ...list.filter((i) => i.id !== item.id)])} />

      {/* Werkzeugleiste: Suche, Kategorien, Tempo/Tonart */}
      <div className="card mb-4 space-y-3 p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative min-w-0 lg:w-80">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
            </svg>
            <input
              placeholder="Suche Titel, Interpret, Album"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input pl-9"
            />
          </div>
          <div className="-mx-1 overflow-x-auto px-1">
            <div className="tabbar" role="tablist">
              {(["", "music", "jingle", "ad", "news", "speech", "voicetrack"] as Array<LibraryCategory | "">).map((c) => (
                <button
                  key={c || "all"}
                  role="tab"
                  aria-selected={category === c}
                  onClick={() => setCategory(c)}
                  className={`tab whitespace-nowrap text-xs sm:text-sm ${category === c ? "tab-active" : ""}`}
                >
                  {c ? CATEGORY_LABEL[c] : "Alle"}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-default pt-3">
          <TaktFilter wert={takt} onChange={setTakt} />
        </div>
      </div>

      <NachrechnenPanel onFertig={load} />

      {error && <Callout tone="danger" className="mb-4">{error}</Callout>}

      {loading ? (
        <div className="card divide-y divide-line">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="h-10 w-10" />
              <div className="flex-1 space-y-1.5"><Skeleton className="h-3.5 w-1/3" /><Skeleton className="h-3 w-1/5" /></div>
              <Skeleton className="hidden h-6 w-32 md:block" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<NoteIcon />}
          title={q || category ? "Nichts gefunden." : "Noch keine Audio-Dateien."}
          description={q || category ? "Andere Suche oder Kategorie probieren." : "Zieh die ersten oben in das Feld."}
        />
      ) : (
        <Card padded={false} className="overflow-hidden">
          <div className="hidden items-center gap-4 border-b border-default bg-surface-sunken px-4 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.06em] text-muted md:flex">
            <span className="flex-1">Titel</span>
            <span className="hidden w-[120px] xl:block">Wellenform</span>
            <span className="w-32">Kategorie</span>
            <span className="w-12 text-right">Dauer</span>
            <span className="w-[248px]" />
          </div>
          <div className="divide-y divide-line">
            {items.map((it) => (
              <LibraryItemRow
                key={it.id}
                item={it}
                onChanged={(updated) => setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)))}
                onDeleted={(id) => setItems((list) => list.filter((i) => i.id !== id))}
              />
            ))}
          </div>
        </Card>
      )}
      </>)}
    </Page>
  );
}

function LibraryItemRow({ item, onChanged, onDeleted }: {
  item: LibraryItem; onChanged: (item: LibraryItem) => void; onDeleted: (id: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setCategory = async (category: LibraryCategory): Promise<void> => {
    setError(null);
    try {
      onChanged(await api.updateLibraryItem(item.id, { category }));
    } catch (err) {
      setError(errorText(err));
    }
  };

  const remove = async (): Promise<void> => {
    if (!window.confirm(`„${item.title}" wirklich löschen? Die Datei ist danach weg.`)) return;
    try {
      await api.deleteLibraryItem(item.id);
      onDeleted(item.id);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className={`px-4 py-3 transition-colors ${open ? "bg-surface-sunken" : "hover:bg-surface-hover"}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:flex-nowrap">
        {/* Cover, Titel und Interpret zweizeilig, darunter Tempo und Messung */}
        <div className="flex min-w-0 flex-1 basis-full items-center gap-3 md:basis-auto">
          <CoverThumb item={item} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-primary">{item.title}</div>
            <div className="truncate text-xs text-muted">{item.artist ?? "Interpret unbekannt"}{item.album ? ` · ${item.album}` : ""}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <TaktBadge item={item} />
              <AnalysisBadge item={item} />
            </div>
          </div>
          <span className="text-xs tabular-nums text-muted md:hidden">{formatDuration(item.duration_ms)}</span>
        </div>
        <div className="hidden opacity-80 xl:block"><Waveform item={item} width={120} height={24} /></div>
        {/* Kategorie als farbiges Etikett – bleibt direkt änderbar */}
        <div className="md:w-32">
          <select
            value={item.category}
            onChange={(e) => void setCategory(e.target.value as LibraryCategory)}
            className={`h-6 max-w-full cursor-pointer rounded-full border-0 py-0 pl-2.5 pr-7 text-[0.72rem] font-medium ${CATEGORY_TONE[item.category]}`}
            title={CATEGORY_HINT[item.category]}
            aria-label="Kategorie"
          >
            {(Object.keys(CATEGORY_LABEL) as LibraryCategory[]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
            ))}
          </select>
        </div>
        <span className="hidden w-12 text-right text-sm tabular-nums text-secondary md:block">{formatDuration(item.duration_ms)}</span>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 md:w-[248px] md:flex-none">
          <audio controls preload="none" src={api.streamUrl(item.id)} className="h-8 min-w-0 max-w-[180px] flex-1 dark:[color-scheme:dark]" />
          <button onClick={() => setOpen((o) => !o)} className={`btn btn-sm ${open ? "btn-secondary" : "btn-ghost"}`} aria-expanded={open}>
            {open ? "Schließen" : "Angaben"}
          </button>
        </div>
      </div>
      {error && <Callout tone="danger" className="mt-2">{error}</Callout>}
      {open && <LibraryDetails item={item} onChanged={onChanged} onDelete={remove} />}
    </div>
  );
}

function LibraryDetails({ item, onChanged, onDelete }: {
  item: LibraryItem; onChanged: (item: LibraryItem) => void; onDelete: () => Promise<void>;
}): JSX.Element {
  const [detail, setDetail] = useState<LibraryItemWithExtras | null>(null);
  const [form, setForm] = useState({
    title: item.title,
    artist: item.artist ?? "",
    album: item.album ?? "",
    composer: item.composer ?? "",
    publisher: item.publisher ?? "",
    label: item.label ?? "",
    isrc: item.isrc ?? "",
    year: item.year ? String(item.year) : "",
  });
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");

  useEffect(() => {
    api.getLibraryItem(item.id).then(setDetail).catch(() => setDetail(null));
  }, [item.id]);

  const save = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setState("saving");
    try {
      const updated = await api.updateLibraryItem(item.id, {
        title: form.title,
        artist: form.artist || null,
        album: form.album || null,
        composer: form.composer,
        publisher: form.publisher,
        label: form.label,
        isrc: form.isrc.replace(/-/g, "").toUpperCase(),
        year: form.year ? Number(form.year) : null,
      });
      onChanged(updated);
      setState("saved");
    } catch (err) {
      setState(errorText(err));
    }
  };

  const field = (key: keyof typeof form, label: string, props: Record<string, unknown> = {}): JSX.Element => (
    <label className="block">
      <span className="field-label">{label}</span>
      <input className="input" value={form[key]}
        onChange={(e) => { setForm({ ...form, [key]: e.target.value }); setState("idle"); }} {...props} />
    </label>
  );

  return (
    <form onSubmit={save} className="mt-3 space-y-4 rounded-xl border border-default bg-card p-4 shadow-sm">
      <div>
        <div className="section-label mb-2">Angaben</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {field("title", "Titel", { required: true })}
          {field("artist", "Interpret")}
          {field("album", "Album")}
        </div>
      </div>
      <div>
        <div className="section-label mb-2">Für Meldelisten (GEMA/GVL)</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {field("composer", "Komponist / Urheber")}
          {field("publisher", "Verlag")}
          {field("label", "Label")}
          {field("isrc", "ISRC", { placeholder: "DEA012600001", maxLength: 15 })}
          {field("year", "Jahr", { type: "number", min: 1900, max: 2100 })}
        </div>
      </div>
      {item.analysis_status === "done" && (
        <div className="well flex flex-wrap items-center gap-4 text-xs text-muted">
          <div className="max-w-full overflow-hidden"><Waveform item={item} width={400} height={48} /></div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            <dt>Lautheit</dt><dd className="tabular-nums text-secondary">{item.loudness_lufs ?? "—"} LUFS, Spitze {item.true_peak_db ?? "—"} dBTP</dd>
            <dt>Einstieg</dt><dd className="tabular-nums text-secondary">bei {formatSeconds(item.cue_in_ms ?? 0)}, Ausstieg bei {formatDuration(item.cue_out_ms)}</dd>
            <dt>Übergang</dt><dd className="tabular-nums text-secondary">Nächster Titel setzt ein bei {formatDuration(item.mix_out_ms)}</dd>
          </dl>
        </div>
      )}
      {item.analysis_status === "failed" && (
        <Callout tone="danger" className="text-xs">
          Die Datei ließ sich nicht lesen und wird deshalb nicht gesendet: {item.analysis_error}
        </Callout>
      )}
      <TaktEditor item={item} onChanged={(patch) => onChanged({ ...item, ...patch })} />
      {detail && detail.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted">
          Tags: {detail.tags.map((t) => <Badge key={t}>{t}</Badge>)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-default pt-4">
        <button type="submit" disabled={state === "saving"} className="btn btn-primary">
          Speichern
        </button>
        <button type="button" onClick={() => void api.reanalyze(item.id).then(() => onChanged({ ...item, analysis_status: "pending" }))}
          className="btn btn-secondary">
          Neu messen
        </button>
        {state === "saved" && <Badge tone="success">Gespeichert.</Badge>}
        {state !== "idle" && state !== "saving" && state !== "saved" && <span className="text-xs text-red-600">{state}</span>}
        <button type="button" onClick={() => void onDelete()} className="btn btn-ghost ml-auto text-red-600 hover:!text-red-700 dark:text-red-400">
          Löschen
        </button>
      </div>
    </form>
  );
}
